import {
    MouseEvent as ReactMouseEvent,
    ReactElement,
    useCallback,
    useMemo,
    useState,
} from 'react'
import AddUser from './AddUser'
import {
    Box,
    Button,
    IconButton,
    TextField,
    Typography,
    InputAdornment,
    Tooltip,
} from '@mui/material'
import {
    MRT_ColumnDef,
    MRT_ColumnFiltersState,
    MRT_RowSelectionState,
    MRT_SortingState,
} from 'material-react-table'
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import {
    deleteUser,
    fetchUsers,
    resendUserInvitation,
    resetUserPassword,
} from '../../services/userServices'
import MRTTable from '../Common/Table'
import SearchIcon from '@mui/icons-material/Search'
import MultiSelectInput from '../Common/MultiSelectInput'
import BulkResendAction from './BulkInvite/BulkResendAction'
import { InvitationStatusFilter, USER_STATUS } from './constants'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import LockResetIcon from '@mui/icons-material/LockReset'
import { getRoleAuthId } from '../../utils/UserManagementUtils'
import { useAppSelector, useAppDispatch } from '../../redux/hook'
import { updateLoader } from '../../redux/slices/loaderSlice'
import theme from '../../theme/theme'
import {
    IformData,
    IOpenAddOrEditUser,
    User,
    UserApiResponse,
} from './interfaces'
import {
    defaultAddUserFormData,
    MODE,
    UserPermissions,
    USER_ROLES,
} from '../../constants/constants'
import { formatDateTimeWithUserPreferences } from '../../utils/DateAndTimeUtils'
import { useTranslation } from 'react-i18next'
import APIToken from '../Layout/Navbar/APIToken'
import ConfirmationDialog from '../Common/ConfirmationDialog'
import { useRoutePermission } from '../../hooks/useRoutePermission'
import { usePermissions } from '../../hooks/usePermissions'
import { useBulkInvitationProgress } from '../../hooks/useBulkInvitationProgress'
import { BulkInviteProgress } from '../../services/bulkInvitationService'
import LinearProgress from '@mui/material/LinearProgress'
import CircularProgress from '@mui/material/CircularProgress'
import { enqueueSnackbar } from 'notistack'

const fetchSize = 30
const actionColumnSize = 35
const lastActionColumnSize = 70

const actionCellSx = {
    backgroundColor: '#E9F6FF',
    borderBottom: '3px solid #FFFFFF',
    borderTop: '3px solid #FFFFFF',
    fontSize: '14px',
    padding: '0px',
    textAlign: 'center',
}

const actionIconButtonSx = {
    backgroundColor: 'transparent',
    height: 28,
    padding: '2px',
    width: 28,
    '&:hover': {
        backgroundColor: 'transparent',
    },
}

const displayColumnDefOptions = {
    'mrt-row-select': {
        header: '',
        size: actionColumnSize,
        minSize: actionColumnSize,
        grow: false,
        maxSize: actionColumnSize,
        muiTableBodyCellProps: {
            sx: {
                ...actionCellSx,
                '& .MuiCheckbox-root': {
                    padding: '2px',
                },
            },
        },
        /**
         * UBQ-5212: the select-all checkbox would not line up with the row checkboxes
         * below it. MRT renders head-cell content as
         *   .Mui-TableHeadCell-Content > .Mui-TableHeadCell-Content-Labels > .-Content-Wrapper
         * all three of which are flex boxes it sizes itself, so `textAlign` alone does
         * nothing and `align: 'center'` makes MRT inject its own padding.
         *
         * Deliberately NO left border here. Table.tsx gives the first body cell a
         * FIRST_CELL_INSET left border via `&:first-of-type`, but that is the table-level
         * `muiTableBodyCellProps`, which the column-level override below replaces for this
         * column — so the select cell has no inset to mirror. Adding one shrinks the head
         * cell's content box by 7px and moves its centre 3.5px right (half the border),
         * which measured as: header centre 122.0 vs row centre 118.5. Both cells are 35px
         * with no border, so both content boxes start at the same x and the two centres
         * coincide by construction.
         */
        muiTableHeadCellProps: {
            sx: {
                padding: '0px',
                /* The body cell centres its checkbox with `textAlign` on a plain block
                   box. Matching that is more reliable than fighting MRT's three nested
                   flex wrappers: with flex the position depends on each wrapper's width,
                   and centring inside a wrapper that has shrunk to its content is a no-op.
                   Forcing them to block + textAlign gives the head cell the same layout
                   mechanism and the same content box as the body. */
                '& .Mui-TableHeadCell-Content, & .Mui-TableHeadCell-Content-Labels, & .Mui-TableHeadCell-Content-Wrapper':
                    {
                        display: 'block',
                        width: '100%',
                        textAlign: 'center',
                        padding: 0,
                        margin: 0,
                    },
                /* Match the body checkbox's padding. At MUI's default 9px the header
                   checkbox is wider than the 35px column and cannot sit centred in it. */
                '& .MuiCheckbox-root': {
                    padding: '2px',
                },
            },
        },
    },
}

const UserManagement = (): ReactElement => {
    const { t } = useTranslation()
    const queryClient = useQueryClient()
    const org = useAppSelector((state) => state.org.org)
    const dispatch = useAppDispatch()
    const roles = useAppSelector((state) => state.roles.roles)
    const userLocalization = useAppSelector(
        (state) => state.user.user?.preferences?.localization,
    )
    const { hasPermission } = usePermissions()
    const hasWriteAccess = hasPermission(UserPermissions.USER_ADMIN)
    const isSuperAdmin = hasPermission(UserPermissions.ORG_ADMIN)
    //checking the permissions, if user doesn't have permission. redirecting to home
    useRoutePermission([
        UserPermissions.USER_ADMIN,
        UserPermissions.SYSTEM_READER,
    ])
    const [openAddOrEditUser, setOpenAddOrEditUser] =
        useState<IOpenAddOrEditUser>({
            mode: MODE.ADD,
            open: false,
            formData: defaultAddUserFormData,
        })
    const [openApiKeyPopUp, setOpenApiKeyPopUp] = useState<boolean>(false)
    const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({})
    /**
     * UBQ-5212 / UBQ-5485: server-side invitation filter. An empty array means "everyone", so the
     * screen still opens on the full member list; the operator can tick one or more statuses to
     * narrow it. Sent to GET /users as a comma-separated `inviteStatus` param.
     */
    const [inviteStatus, setInviteStatus] = useState<InvitationStatusFilter[]>(
        [],
    )
    /** UBQ-5485: options for the multi-select status filter; ids match the backend InvitationState. */
    const inviteStatusOptions = useMemo<{ id: string; name: string }[]>(
        () => [
            {
                id: InvitationStatusFilter.INVITED,
                name: t('invitationStatusInvited'),
            },
            {
                id: InvitationStatusFilter.NOT_INVITED,
                name: t('invitationStatusNotInvited'),
            },
            {
                id: InvitationStatusFilter.ACCEPTED,
                name: t('invitationStatusActive'),
            },
        ],
        [t],
    )
    // True when the operator asked for "every invited user in this account" rather than ticking rows, so
    // the server resolves the population instead of the client shipping hundreds of ids.
    const [selectedRowData, setSelectedRowData] = useState<User>()
    const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
        [],
    )
    const [globalFilter, setGlobalFilter] = useState<string>()
    const [sorting, setSorting] = useState<MRT_SortingState>([])
    const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false)

    const invalidateUsers = useCallback(() => {
        queryClient.invalidateQueries({
            queryKey: ['user-management-table-data'],
        })
    }, [queryClient])

    const handleAddOrEditUser = useCallback(
        (refreshTable: boolean) => {
            if (refreshTable) {
                invalidateUsers()
                setRowSelection({})
            }
            setOpenAddOrEditUser({
                mode: MODE.ADD,
                open: false,
                formData: defaultAddUserFormData,
            })
        },
        [invalidateUsers],
    )

    const handleOpen = useCallback((mode: MODE, data: IformData) => {
        setOpenAddOrEditUser({
            mode,
            open: true,
            formData: data,
        })
    }, [])

    const { data, fetchNextPage, isError, isFetching, isLoading } =
        useInfiniteQuery<UserApiResponse>({
            queryKey: [
                'user-management-table-data',
                columnFilters, // refetch when columnFilters changes
                globalFilter, // refetch when globalFilter changes
                sorting, // refetch when sorting changes
                inviteStatus, // refetch when the invitation filter changes
                org,
            ],
            queryFn: async ({ pageParam }) => {
                let filterBy = sorting[0]?.id
                let sortOrder = ''
                if (filterBy) {
                    sortOrder = sorting[0]?.desc ? 'DESC' : 'ASC'
                }
                if (filterBy === 'createdAt') {
                    filterBy = 'CREATED'
                }
                if (
                    org?.id &&
                    hasPermission([
                        UserPermissions.USER_ADMIN,
                        UserPermissions.SYSTEM_READER,
                    ])
                ) {
                    const response = await fetchUsers({
                        pageNumber: (pageParam as number) + 1,
                        size: fetchSize,
                        searchText: globalFilter?.trim() ?? '',
                        filterBy: filterBy ? filterBy.toUpperCase() : '',
                        sortOrder,
                        orgId: org?.id,
                        // UBQ-5485: joined to a CSV string; omitted entirely when nothing is
                        // ticked, so the default request is unchanged.
                        inviteStatus: inviteStatus.length
                            ? inviteStatus.join(',')
                            : undefined,
                    })
                    return response as UserApiResponse
                } else {
                    return null as unknown as UserApiResponse
                }
            },
            initialPageParam: 0,
            getNextPageParam: (lastPage, pages) => {
                // Check if we got any data
                if (!lastPage?.usersModels?.length) {
                    return undefined
                }

                // Calculate if we've received all data
                const totalFetched = pages.reduce(
                    (acc, page) => acc + (page?.usersModels?.length || 0),
                    0,
                )
                if (totalFetched >= (lastPage?.meta?.totalRowCount || 0)) {
                    return undefined
                }

                return pages.length
            },
            refetchOnWindowFocus: false,
        })
    const flatData = useMemo(
        () =>
            data?.pages?.flatMap((page) =>
                page?.usersModels ? page?.usersModels : [],
            ) ?? [],
        [data],
    )

    const totalDBRowCount = useMemo(
        () => data?.pages?.[0]?.meta?.totalRowCount ?? 0,
        [data],
    )
    const totalDBRowCountWithoutFilters = useMemo(
        () => data?.pages?.[0]?.meta?.totalCount ?? 0,
        [data],
    )

    const openAddUser = useCallback(() => {
        handleOpen(MODE.ADD, {
            ...defaultAddUserFormData,
            totalUsers: totalDBRowCountWithoutFilters,
        })
    }, [totalDBRowCountWithoutFilters, handleOpen])
    const openEditUser = useCallback(
        (rowData: User) => {
            handleOpen(MODE.EDIT, {
                email: rowData.email,
                role: rowData.role,
                id: rowData.id,
                orgId: rowData.orgId,
                totalUsers: totalDBRowCountWithoutFilters,
                units: rowData.preferences.units,
                localization: rowData?.preferences?.localization,
            })
        },
        [totalDBRowCountWithoutFilters, handleOpen],
    )

    const openUserDeleteConfirmation = useCallback(() => {
        setOpenDeleteDialog(true)
    }, [])
    const closeUserDeleteConfirmation = useCallback(() => {
        setOpenDeleteDialog(false)
    }, [])

    /**
     * UBQ-5212: map the MRT selection to ids immediately. rowSelection is keyed by row
     * *index* into flatData, so it is only meaningful against the current page ordering —
     * resolve it before any refetch re-sorts the list. NOTE: User.id is typed number but
     * the API returns a uuid string.
     *
     * Only users still awaiting sign-in are included. Selecting an Active user must not
     * arm the resend action: the server would skip them anyway, but an enabled button that
     * then reports "0 to send" reads as a bug. Deactivated users are excluded for the same
     * reason — they must never be re-invited.
     *
     * Device Managers are excluded unless the operator is a super admin, matching the
     * per-row resend action below (see the userActions column). Without this, ticking a
     * pending Device Manager row — or using select-all — re-invites a user the per-row
     * button deliberately hides.
     */
    const selectedResendableIds: string[] = useMemo(
        () =>
            Object.keys(rowSelection)
                .map((key) => flatData[Number(key)])
                .filter(Boolean)
                .filter(
                    (user) =>
                        user.status === USER_STATUS.INVITED ||
                        user.status === USER_STATUS.NOT_INVITED,
                )
                .filter(
                    (user) =>
                        isSuperAdmin || user.role !== USER_ROLES.DEVICE_MANAGER,
                )
                .map((user) => String(user.id)),
        [rowSelection, flatData, isSuperAdmin],
    )

    const clearSelection = useCallback(() => {
        setRowSelection({})
    }, [])

    /**
     * UBQ-5486: follow a bulk invitation to completion and refresh once, at the end.
     *
     * Both Add User and the bulk resend hand their batch here rather than refreshing themselves. The
     * server paces the sends, so a refresh on the POST response listed the account as it was before any
     * invitation had gone out — the reported "only one of the created users appears".
     */
    const onBatchFinished = useCallback(
        (finished: BulkInviteProgress) => {
            invalidateUsers()
            clearSelection()
            /* Both messages are phrased "{{sent}} of {{total}}" so they read correctly at any number.
               "Sent {{sent}} invitations" became "Sent 1 invitations" for a single-user resend, which
               takes this async path too. Solved with wording rather than i18next plurals, which would
               need _one/_other variants of every key in all seven locales. */
            if (finished.failed > 0) {
                enqueueSnackbar(
                    t('bulkInviteFinishedWithFailures', {
                        sent: finished.sent,
                        total: finished.total,
                        failed: finished.failed,
                    }),
                    { variant: 'warning' },
                )
            } else {
                enqueueSnackbar(
                    t('bulkInviteFinished', {
                        sent: finished.sent,
                        total: finished.total,
                    }),
                    { variant: 'success' },
                )
            }
        },
        [t, invalidateUsers, clearSelection],
    )

    const {
        progress: batchProgress,
        isRunning: batchRunning,
        track: trackBatch,
    } = useBulkInvitationProgress({
        orgId: org?.id ?? '',
        onFinished: onBatchFinished,
        // Pick a batch back up after a reload, so a long send is not lost by navigating away.
        reattach: true,
    })

    /**
     * UBQ-5544: what the progress banner should say while the initial batch is queuing. A bulk add can
     * either email the new users or just create them, and a resend only emails — the banner names which.
     * Cleared implicitly: it is only read while a batch is running, and each new batch resets it first.
     */
    const [batchKind, setBatchKind] = useState<
        'add-invite' | 'add-only' | 'reinvite' | null
    >(null)

    const handleAddUserBatchStarted = useCallback(
        (batchId: string, startedAt: number, sendInvitation: boolean) => {
            setBatchKind(sendInvitation ? 'add-invite' : 'add-only')
            trackBatch(batchId, startedAt)
        },
        [trackBatch],
    )

    const handleResendBatchStarted = useCallback(
        (batchId: string, startedAt: number) => {
            setBatchKind('reinvite')
            trackBatch(batchId, startedAt)
        },
        [trackBatch],
    )

    /**
     * UBQ-5544: the banner text for the running batch. Both the queuing phase and the counted progress
     * phase name the same operation — add-and-invite, add-only, or a plain resend — so the wording never
     * flips from "Adding Users…" back to the generic "Sending invitations…" mid-run.
     */
    const bannerText = useMemo(() => {
        const done = (batchProgress?.sent ?? 0) + (batchProgress?.failed ?? 0)
        const total = batchProgress?.total ?? 0
        const inProgress = total > 0
        switch (batchKind) {
            case 'add-invite':
                return inProgress
                    ? t('bulkInviteAddingAndSendingProgress', { done, total })
                    : t('bulkInviteAddingAndSending')
            case 'add-only':
                return inProgress
                    ? t('bulkInviteAddingWithoutSendingProgress', {
                          done,
                          total,
                      })
                    : t('bulkInviteAddingWithoutSending')
            default:
                return inProgress
                    ? t('bulkInviteSendingProgress', { done, total })
                    : t('bulkInviteSending')
        }
    }, [batchKind, batchProgress, t])

    const batchPercent = useMemo(() => {
        if (!batchProgress?.total) return 0
        return Math.min(
            100,
            Math.round(
                ((batchProgress.sent + batchProgress.failed) /
                    batchProgress.total) *
                    100,
            ),
        )
    }, [batchProgress])

    // UBQ-5212: the row selection must not survive a change of search or column filter. MRT keys
    // rowSelection by row *index* into flatData, so once the query returns a different set those
    // indices point at different people — or at nobody. Select row 25, then search something that
    // returns 3 rows, and the ticks that remain are meaningless. Delete acts on exactly those indices.
    // Wrapped rather than reset in an effect so both entry points are covered: the search box above
    // and MRT's own column-filter UI.
    const handleGlobalFilterChange: React.Dispatch<
        React.SetStateAction<string | undefined>
    > = useCallback(
        (value) => {
            setGlobalFilter(value)
            clearSelection()
        },
        [clearSelection],
    )

    const handleColumnFiltersChange: React.Dispatch<
        React.SetStateAction<MRT_ColumnFiltersState>
    > = useCallback(
        (value) => {
            setColumnFilters(value)
            clearSelection()
        },
        [clearSelection],
    )

    const deleteUsersHandler = useCallback(async () => {
        /* Resolved defensively: rowSelection is keyed by row index into flatData, so an
           index left over from a wider result set resolves to undefined and `.id` on it
           throws — taking the dialog down with an unhandled TypeError rather than deleting
           anything. The same guard the resend selection uses. Row selection is now also
           cleared whenever the filter changes, so this is a backstop. */
        const usersToDelete: number[] = Object.keys(rowSelection)
            .map((key) => flatData[Number(key)])
            .filter(Boolean)
            .map((user) => user.id)

        if (usersToDelete.length === 0) {
            setOpenDeleteDialog(false)
            return
        }

        await deleteUser(usersToDelete, org!.id)

        setRowSelection({})
        invalidateUsers()
        setOpenDeleteDialog(false)
    }, [flatData, org, rowSelection, invalidateUsers])

    const apiKeyHandler = useCallback((rowData: User) => {
        setOpenApiKeyPopUp(true)
        setSelectedRowData(rowData)
    }, [])

    const resendInvitationHandler = useCallback(
        async (rowData: User) => {
            dispatch(updateLoader(true))
            try {
                await resendUserInvitation(
                    {
                        id: rowData.id,
                        roleAuth0Id: getRoleAuthId(roles, rowData.role),
                    },
                    org?.id || '',
                )
                if (rowData.status === USER_STATUS.NOT_INVITED) {
                    invalidateUsers()
                }
            } finally {
                dispatch(updateLoader(false))
            }
        },
        [roles, org?.id, dispatch, invalidateUsers],
    )

    const resetPasswordHandler = useCallback(
        async (rowData: User) => {
            dispatch(updateLoader(true))
            try {
                await resetUserPassword(
                    {
                        email: rowData.email,
                        userId: rowData.id,
                    },
                    org?.id || '',
                )
            } finally {
                dispatch(updateLoader(false))
            }
        },
        [org?.id, dispatch],
    )

    const disableDeleteButton = useMemo(
        () => Object.keys(rowSelection).length < 1,
        [rowSelection],
    )
    const columns: MRT_ColumnDef<User>[] = useMemo(
        () => [
            {
                header: '',
                accessorKey: 'editid',
                id: 'editid',
                Cell: ({ row }) => (
                    <IconButton
                        id={`row-${row.id}-edit-button`}
                        onClick={(event) => {
                            event.stopPropagation()
                            openEditUser(row.original)
                        }}
                        aria-controls="simple-menu-preview"
                        aria-haspopup="true"
                        aria-label={
                            hasWriteAccess ? t('editUser') : t('viewUser')
                        }
                        sx={actionIconButtonSx}
                        title={hasWriteAccess ? t('editUser') : t('viewUser')}
                    >
                        {hasWriteAccess ? (
                            <img
                                src="/assets/images/icons/edit.svg"
                                alt={t('editUser')}
                            />
                        ) : (
                            <img
                                src="/assets/images/icons/preview.svg"
                                alt={t('viewUser')}
                            />
                        )}
                    </IconButton>
                ),
                size: actionColumnSize,
                minSize: actionColumnSize,
                grow: false,
                maxSize: actionColumnSize,
                enableResizing: false,
                enableSorting: false,
                enableColumnFilter: false,
                muiTableBodyCellProps: {
                    role: 'gridcell',
                    sx: actionCellSx,
                    onClick: (event: ReactMouseEvent) =>
                        event.stopPropagation(),
                },
            },
            ...(isSuperAdmin
                ? [
                      {
                          header: '',
                          accessorKey: 'apiKeyId',
                          id: 'apiKeyId',
                          Cell: ({
                              row,
                          }: {
                              row: { id: string; original: User }
                          }) => {
                              const isApiKeyDisabled =
                                  row.original.status === USER_STATUS.INVITED ||
                                  row.original.status ===
                                      USER_STATUS.NOT_INVITED

                              return (
                                  <IconButton
                                      id={`row-${row.id}-api-key-button`}
                                      onClick={(event) => {
                                          event.stopPropagation()
                                          apiKeyHandler(row.original)
                                      }}
                                      disabled={isApiKeyDisabled}
                                      aria-label={t('generateAPIToken')}
                                      aria-disabled={isApiKeyDisabled}
                                      sx={{
                                          ...actionIconButtonSx,
                                          filter: isApiKeyDisabled
                                              ? 'grayscale(100%)'
                                              : 'none',
                                          opacity: isApiKeyDisabled ? 0 : 1,
                                      }}
                                      title={t('generateAPIToken')}
                                  >
                                      <img
                                          src="/assets/images/icons/access_key.svg"
                                          alt={t('generateAPIToken')}
                                      />
                                  </IconButton>
                              )
                          },
                          size: actionColumnSize,
                          minSize: actionColumnSize,
                          grow: false,
                          maxSize: actionColumnSize,
                          enableResizing: false,
                          enableSorting: false,
                          enableColumnFilter: false,
                          muiTableBodyCellProps: {
                              role: 'gridcell',
                              sx: actionCellSx,
                              onClick: (event: ReactMouseEvent) =>
                                  event.stopPropagation(),
                          },
                      } as MRT_ColumnDef<User>,
                  ]
                : []),
            {
                accessorKey: 'email',
                header: t('userEmail'),
            },
            {
                id: 'role',
                accessorFn: (row) =>
                    row.role[0].toUpperCase() + row.role.substring(1),
                header: t('role'),
            },
            {
                accessorKey: 'status',
                header: t('status'),
                enableFilterMatchHighlighting: false,
            },
            {
                id: 'createdAt',
                accessorFn: (row) => {
                    if (row.createdAt) {
                        return formatDateTimeWithUserPreferences(
                            row.createdAt,
                            userLocalization,
                        )
                    } else {
                        return ''
                    }
                },
                header: t('createdAt'),
                enableFilterMatchHighlighting: false,
            },
            {
                accessorKey: 'name',
                header: t('invitedBy'),
            },
            ...(hasWriteAccess
                ? [
                      {
                          header: '',
                          accessorKey: 'userActions',
                          id: 'userActions',
                          Cell: ({
                              row,
                          }: {
                              row: { id: string; original: User }
                          }) => {
                              const { status, role } = row.original
                              const isResend =
                                  status === USER_STATUS.INVITED ||
                                  status === USER_STATUS.NOT_INVITED
                              const isFirstSend =
                                  status === USER_STATUS.NOT_INVITED
                              const isReset = status === USER_STATUS.ACTIVE
                              // Device Manager users may only be resent an
                              // invitation / reset by a super admin; hide the
                              // action for everyone else.
                              const isRestrictedRole =
                                  role === USER_ROLES.DEVICE_MANAGER &&
                                  !isSuperAdmin
                              if ((!isResend && !isReset) || isRestrictedRole) {
                                  return null
                              }
                              return (
                                  <Tooltip
                                      title={
                                          isResend
                                              ? isFirstSend
                                                  ? t('sendInvitation')
                                                  : t('resendInvitation')
                                              : t('sendResetPasswordEmail')
                                      }
                                      placement="top"
                                  >
                                      <IconButton
                                          id={`row-${row.id}-${
                                              isResend
                                                  ? isFirstSend
                                                      ? 'send-invitation'
                                                      : 'resend-invitation'
                                                  : 'reset-password'
                                          }-button`}
                                          onClick={(event) => {
                                              event.stopPropagation()
                                              if (isResend) {
                                                  resendInvitationHandler(
                                                      row.original,
                                                  )
                                              } else {
                                                  resetPasswordHandler(
                                                      row.original,
                                                  )
                                              }
                                          }}
                                          aria-label={
                                              isResend
                                                  ? isFirstSend
                                                      ? t('sendInvitation')
                                                      : t('resendInvitation')
                                                  : t('sendResetPasswordEmail')
                                          }
                                          sx={actionIconButtonSx}
                                      >
                                          {isResend ? (
                                              <MailOutlineIcon
                                                  fontSize="small"
                                                  sx={{
                                                      color: theme.palette
                                                          .primary.main,
                                                  }}
                                              />
                                          ) : (
                                              <LockResetIcon
                                                  fontSize="small"
                                                  sx={{
                                                      color: theme.palette
                                                          .primary.main,
                                                  }}
                                              />
                                          )}
                                      </IconButton>
                                  </Tooltip>
                              )
                          },
                          size: lastActionColumnSize,
                          minSize: lastActionColumnSize,
                          grow: false,
                          maxSize: lastActionColumnSize,
                          enableResizing: false,
                          enableSorting: false,
                          enableColumnFilter: false,
                          muiTableBodyCellProps: {
                              role: 'gridcell',
                              sx: actionCellSx,
                              onClick: (event: ReactMouseEvent) =>
                                  event.stopPropagation(),
                          },
                      } as MRT_ColumnDef<User>,
                  ]
                : []),
        ],
        [
            apiKeyHandler,
            hasWriteAccess,
            isSuperAdmin,
            openEditUser,
            resendInvitationHandler,
            resetPasswordHandler,
            t,
            userLocalization,
        ],
    )

    return (
        <Box
            sx={{
                margin: '30px',
                border: `1px solid ${theme.palette.primary.main}`,
                borderRadius: '20px',
                maxHeight: `calc(100vh - 199px)`,
                overflow: 'auto',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 3,
                }}
            >
                <Box>
                    <Typography variant="h4" component="h2">
                        {t('users')}
                    </Typography>
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <Box
                        sx={{
                            background: '#E3F4FF',
                            borderRadius: '25px',
                            marginRight: '10px',
                        }}
                    >
                        <TextField
                            id="user-list-search"
                            aria-label={t('searchUsers')}
                            sx={{
                                padding: 0,
                                '& fieldset': { border: 'none' },
                            }}
                            placeholder={t('search')}
                            value={globalFilter}
                            onChange={(e) =>
                                handleGlobalFilterChange(e.target.value)
                            }
                            className="table-search-bar"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <IconButton
                                            sx={{
                                                '&:hover ': {
                                                    background: 'none',
                                                    cursor: 'auto',
                                                },
                                            }}
                                        >
                                            <SearchIcon
                                                sx={{ color: 'primary' }}
                                            />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                                style: {
                                    height: 36,
                                    paddingLeft: '5px',
                                },
                            }}
                        />
                    </Box>
                    {/* UBQ-5485: multi-select status filter with checkboxes (no search), matching
                        the CCL AlarmStatus / ShipmentStatusList pattern; an empty selection means
                        everyone. */}
                    <Box
                        sx={{
                            background: '#E3F4FF',
                            borderRadius: '25px',
                            marginRight: '10px',
                            minWidth: 170,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '25px',
                                height: 36,
                            },
                            '& fieldset': { border: 'none' },
                        }}
                    >
                        <MultiSelectInput
                            id="user-invite-status-filter"
                            label={t('status')}
                            isLegend={true}
                            value={inviteStatus}
                            onChange={(_id: string, values: string[]) => {
                                setInviteStatus(
                                    values as InvitationStatusFilter[],
                                )
                                clearSelection()
                            }}
                            list={inviteStatusOptions}
                            displayInputLabel={false}
                            renderLabelInValue={true}
                            displayEmpty={true}
                        />
                    </Box>
                    {openApiKeyPopUp && (
                        <APIToken
                            handleClose={() => setOpenApiKeyPopUp(false)}
                            userId={selectedRowData?.id}
                            userName={selectedRowData?.email}
                        />
                    )}
                    {hasWriteAccess ? (
                        <>
                            {/* UBQ-5212: the per-row resend icon, promoted to act on the selection. */}
                            <BulkResendAction
                                orgId={org?.id ?? ''}
                                // Trimmed because org names carry stray trailing spaces in real data.
                                // The name sits mid-sentence in the confirm dialog, and a trailing space
                                // gives the line somewhere to break, stranding the full stop.
                                accountName={org?.name?.trim()}
                                selectedUserIds={selectedResendableIds}
                                onChanged={invalidateUsers}
                                onCompleted={clearSelection}
                                onBatchStarted={handleResendBatchStarted}
                                batchRunning={batchRunning}
                            />
                            <Box
                                sx={{
                                    cursor: disableDeleteButton
                                        ? 'not-allowed'
                                        : 'default',
                                }}
                            >
                                <Button
                                    onClick={openUserDeleteConfirmation}
                                    disabled={disableDeleteButton}
                                    aria-label={t('deleteSelectedUser')}
                                    aria-disabled={disableDeleteButton}
                                    sx={{
                                        minWidth: '25px',
                                        maxWidth: '30px',
                                        marginLeft: '5px',
                                        filter: disableDeleteButton
                                            ? 'grayscale(100%)'
                                            : 'none',
                                        opacity: disableDeleteButton ? 0.5 : 1,
                                    }}
                                    id="delete-user-button"
                                >
                                    <img
                                        src={`/assets/images/icons/delete-icon.svg`}
                                        alt={t('deleteUser')}
                                    />
                                </Button>
                            </Box>
                            <Button
                                onClick={openAddUser}
                                sx={{
                                    minWidth: '25px',
                                    maxWidth: '30px',
                                    marginLeft: '15px',
                                    minHeight: '25px',
                                    maxHeight: '30px',
                                    marginRight: '10px',
                                }}
                                id="add-user-button"
                                aria-label={t('addUser')}
                                aria-disabled={disableDeleteButton}
                            >
                                <img
                                    src={`/assets/images/icons/add-icon.svg`}
                                    alt={t('addUser')}
                                />
                            </Button>
                        </>
                    ) : null}
                    {openAddOrEditUser.open && (
                        <AddUser
                            openAddOrEditUser={openAddOrEditUser}
                            handleAddOrEditUser={handleAddOrEditUser}
                            readOnly={!hasWriteAccess}
                            onBatchStarted={handleAddUserBatchStarted}
                        />
                    )}
                </Box>
            </Box>
            {/* UBQ-5486: the invitations are still going out at this point. Without this the screen
                looks idle and the account looks short of the users that were just added. */}
            {batchRunning && (
                <Box
                    id="bulk-invite-progress"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        mx: 3,
                        mb: 2,
                        px: 2,
                        py: 1.25,
                        background: '#E3F4FF',
                        borderRadius: '8px',
                    }}
                >
                    <CircularProgress size={16} />
                    <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                        {bannerText}
                    </Typography>
                    <LinearProgress
                        variant="determinate"
                        value={batchPercent}
                        sx={{ flexGrow: 1, height: 6, borderRadius: 999 }}
                    />
                </Box>
            )}
            <MRTTable<User, UserApiResponse>
                key={JSON.stringify(userLocalization) || 'default'}
                columns={columns}
                sorting={sorting}
                globalFilter={globalFilter}
                columnFilters={columnFilters}
                setRowSelection={setRowSelection}
                rowSelection={rowSelection}
                setColumnFilters={handleColumnFiltersChange}
                setGlobalFilter={handleGlobalFilterChange}
                setSorting={setSorting}
                data={flatData}
                totalDBRowCount={totalDBRowCount}
                totalDBRowCountWithoutFilters={totalDBRowCountWithoutFilters}
                fetchNextPage={fetchNextPage}
                isError={isError}
                isFetching={isFetching}
                isLoading={isLoading}
                enableBottomToolbar={true}
                isCurvedBottomToolbar={true}
                enableRowSelection={hasWriteAccess}
                // UBQ-5212: header select-all, so a page of pending invitations can be re-sent without
                // ticking each row. Scoped to this table — see Table.tsx for why it is opt-in.
                enableSelectAll={hasWriteAccess}
                displayColumnDefOptions={displayColumnDefOptions}
                initialState={{ columnPinning: { right: ['userActions'] } }}
                maxHeight="calc(100vh - 348px)"
            />
            <ConfirmationDialog
                openDialog={openDeleteDialog}
                handleComfirm={deleteUsersHandler}
                handleClose={closeUserDeleteConfirmation}
                DialogHeaderText={t('confirmDelete')}
                // UBQ-5212: state the count. With select-all enabled on this grid one click can tick
                // every loaded row, so a generic "delete users?" no longer says what is at stake.
                DialogBodyText={t('deleteUsersConfirmationCount', {
                    count: Object.keys(rowSelection).length,
                })}
            />
        </Box>
    )
}

export default UserManagement
